import React from "react";
import { motion } from "framer-motion";
import {
  FaTrophy,
  FaUsers,
  FaClock,
  FaHeart,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";

const AboutPage = () => {
  const stats = [
    { icon: <FaTrophy />, value: "500+", label: "Products Sold" },
    { icon: <FaUsers />, value: "10k+", label: "Happy Customers" },
    { icon: <FaClock />, value: "5+", label: "Years Experience" },
    { icon: <FaHeart />, value: "100%", label: "Customer Satisfaction" },
  ];

  const values = [
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: "Quality Assurance",
      description:
        "We ensure every product meets our strict quality standards before reaching you.",
    },
    {
      icon: <FaRocket className="text-3xl" />,
      title: "Innovation First",
      description:
        "Always bringing you the latest and most innovative mobile technology.",
    },
    {
      icon: <FaUsers className="text-3xl" />,
      title: "Customer Centric",
      description:
        "Your satisfaction is our top priority. We put customers first in everything.",
    },
  ];

  return (
    <div className="py-12">
      <div className="container-custom">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            About Waziri Mobile
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Your trusted partner for premium smartphones and accessories since
            2019
          </p>
        </motion.div>

        {/* Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold mb-4">Our Story</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              Waziri Mobile was founded in 2019 with a simple mission: to
              provide the best mobile technology at the most competitive prices.
              Starting as a small local shop, we've grown into one of the most
              trusted mobile retailers in Pakistan.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              We believe that everyone deserves access to the latest technology
              without breaking the bank. That's why we work directly with
              authorized distributors to bring you genuine products with full
              manufacturer warranty.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              Today, we serve thousands of satisfied customers who trust us for
              their mobile needs. Our commitment to quality, transparency, and
              customer service remains stronger than ever.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <img
              src="/Shop.jpeg"
              alt="Our Team"
              className="rounded-2xl shadow-hard w-full max-w-md mx-auto object-cover h-80"
            />
            <div className="absolute -bottom-6 -left-6 bg-primary-600 text-white p-4 rounded-2xl shadow-lg">
              <p className="text-2xl font-bold">5+ Years</p>
              <p className="text-sm">Of Excellence</p>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6 text-center"
            >
              <div className="text-4xl text-primary-600 mb-3 flex justify-center">
                {stat.icon}
              </div>
              <div className="text-3xl font-bold text-gray-800 dark:text-white">
                {stat.value}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">Our Core Values</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="text-center p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-soft"
              >
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center text-primary-600 mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-8 md:p-12 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Why Choose Waziri Mobile?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Experience the difference with Pakistan's most trusted mobile
              retailer
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-2xl font-bold">100%</div>
                <div className="text-sm">Genuine Products</div>
              </div>
              <div>
                <div className="text-2xl font-bold">1 Year</div>
                <div className="text-sm">Warranty Included</div>
              </div>
              <div>
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-sm">Customer Support</div>
              </div>
              <div>
                <div className="text-2xl font-bold">Free</div>
                <div className="text-sm">Delivery Available</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
